import { useState, useEffect, useCallback } from "react";
import { Users, X, Router, Wifi, CircleDot, MapPin, Gauge, Phone, Mail, Home, Power, ExternalLink, Receipt, TriangleAlert, TerminalSquare } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, YAxis, Tooltip as RTooltip } from "recharts";
import { toast } from "sonner";
import api from "@/lib/api";
import { OltProvision } from "@/components/OltProvision";
import { useI18n } from "@/context/I18nContext";

const P = {
  model: "InternetGatewayDevice.DeviceInfo.ModelName",
  fw: "InternetGatewayDevice.DeviceInfo.SoftwareVersion",
  ip: "InternetGatewayDevice.WANDevice.1.WANConnectionDevice.1.WANIPConnection.1.ExternalIPAddress",
  ssid: "InternetGatewayDevice.LANDevice.1.WLANConfiguration.1.SSID",
};
const isOnline = (d) => !!d?.last_inform && Date.now() - new Date(d.last_inform).getTime() < 15 * 60 * 1000;

const Card = ({ icon: Icon, title, tint, children, testid }) => (
  <div data-testid={testid} className="kv-glass rounded-xl p-4 flex-1 min-w-[240px]">
    <div className={`flex items-center gap-2 mb-3 text-sm font-semibold ${tint}`} style={{ fontFamily: "'Manrope',sans-serif" }}>
      <Icon size={16} /> {title}
    </div>
    <div className="space-y-1.5 text-[12px]">{children}</div>
  </div>
);
const Field = ({ label, value }) => (
  <div className="flex justify-between gap-3">
    <span className="text-slate-500 shrink-0">{label}</span>
    <span className="text-slate-200 text-right truncate">{value || "—"}</span>
  </div>
);

export default function Client360Panel({ onClose, initialClientId }) {
  const { t } = useI18n();
  const [clients, setClients] = useState([]);
  const [sel, setSel] = useState(null);
  const [cpes, setCpes] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [faults, setFaults] = useState([]);
  const [topo, setTopo] = useState(null);
  const [rxHist, setRxHist] = useState([]);
  const [rxRange, setRxRange] = useState("30d");
  const [prov, setProv] = useState(null);

  const load = useCallback(() => {
    api.get("/clients").then((r) => setClients(r.data || [])).catch(() => setClients([]));
    api.get("/tr069/devices").then((r) => setCpes(r.data || [])).catch(() => setCpes([]));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (initialClientId && clients.length) {
      const m = clients.find((x) => x.id === initialClientId);
      if (m) setSel(m);
    }
  }, [initialClientId, clients]);
  useEffect(() => {
    if (sel?.id) {
      api.get(`/invoices?client_id=${sel.id}`).then((r) => setInvoices(r.data || [])).catch(() => setInvoices([]));
      api.get("/faults").then((r) => setFaults((r.data || []).filter((f) => f.client_id === sel.id && !["rozwiązana", "zamknięta"].includes(f.status)))).catch(() => setFaults([]));
      api.get(`/clients/${sel.id}/topology`).then((r) => setTopo(r.data || null)).catch(() => setTopo(null));
    } else { setInvoices([]); setFaults([]); setTopo(null); setRxHist([]); }
  }, [sel]);
  useEffect(() => {
    if (sel?.id) api.get(`/clients/${sel.id}/rx-history?range=${rxRange}`).then((r) => setRxHist(r.data || [])).catch(() => setRxHist([]));
  }, [sel, rxRange]);

  const c = sel || {};
  const cpe = cpes.find((d) => d.client_id === c.id);
  const p = cpe?.params || {};
  const RX_META = { ok: ["bg-green-400", t("W normie")], ostrzezenie: ["bg-amber-400", t("Ostrzeżenie")], krytyczny: ["bg-red-500", t("Krytyczny")], brak: ["bg-slate-600", t("Brak pomiaru")] };
  const [rxDot, rxLabel] = RX_META[topo?.rx_status || "brak"] || RX_META.brak;
  const rxTitle = `${rxLabel}: ${topo?.measured_rx_dbm ?? "—"} dBm (oczek. ${topo?.expected_rx_dbm ?? "—"}, min ${topo?.min_rx_dbm ?? "—"})`;
  const reboot = async () => {
    if (!cpe) return;
    try { await api.post(`/tr069/devices/${cpe.id}/tasks`, { type: "reboot" }); toast.success(t("Zlecono restart routera")); }
    catch { toast.error(t("Błąd zlecenia restartu")); }
  };
  const showOnMap = () => {
    if (c.lat && c.lng) { window.dispatchEvent(new CustomEvent("fp-flyto", { detail: { lat: Number(c.lat), lng: Number(c.lng) } })); onClose?.(); }
    else toast.info(t("Brak współrzędnych klienta"));
  };

  return (
    <div className="absolute inset-0 flex flex-col h-full" data-testid="client360-panel">
      <div className="flex items-center justify-between px-4 py-3 kv-glass border-0 border-b border-cyan-500/15">
        <div className="flex items-center gap-2 text-slate-100 font-bold" style={{ fontFamily: "'Manrope',sans-serif" }}>
          <Users size={18} className="text-cyan-400" /> {t("Widok 360° klienta")}
        </div>
        <button onClick={onClose} data-testid="client360-close" className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400"><X size={18} /></button>
      </div>

      <div className="px-4 py-2.5 border-b border-slate-800">
        <select value={c.id || ""} onChange={(e) => setSel(clients.find((x) => x.id === e.target.value) || null)} data-testid="client360-select"
          className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-2 text-sm text-slate-200">
          <option value="">{clients.length ? t("Wybierz klienta…") : t("Brak klientów")}</option>
          {clients.map((x) => <option key={x.id} value={x.id}>{x.name || x.address || x.id}</option>)}
        </select>
      </div>

      <div className="flex-1 overflow-auto p-4">
        {!sel ? (
          <div className="text-slate-500 text-sm text-center mt-8">{t("Wybierz klienta, aby zobaczyć pełny obraz.")}</div>
        ) : (
          <div className="flex flex-wrap gap-3">
            <Card icon={Home} title={t("Klient")} tint="text-emerald-400" testid="card-client">
              <Field label={t("Nazwa")} value={c.name} />
              <Field label={t("Adres")} value={c.address} />
              <Field label={<span className="flex items-center gap-1"><Phone size={11} /> {t("Telefon")}</span>} value={c.phone} />
              <Field label={<span className="flex items-center gap-1"><Mail size={11} /> Email</span>} value={c.email} />
              <Field label={<span className="flex items-center gap-1"><Gauge size={11} /> {t("Plan")}</span>} value={c.plan || c.speed} />
              <Field label={t("Status")} value={c.status} />
            </Card>

            <Card icon={Router} title={t("Router (TR-069)")} tint="text-sky-400" testid="card-router">
              {cpe ? (
                <>
                  <div className="flex items-center gap-1.5 mb-1">
                    <CircleDot size={12} className={isOnline(cpe) ? "text-green-400" : "text-slate-600"} />
                    <span className={isOnline(cpe) ? "text-green-300" : "text-slate-400"}>{isOnline(cpe) ? t("online") : t("offline")}</span>
                  </div>
                  <Field label={t("Model")} value={p[P.model] || cpe.model} />
                  <Field label="S/N" value={cpe.serial} />
                  <Field label="IP WAN" value={p[P.ip]} />
                  <Field label={<span className="flex items-center gap-1"><Wifi size={11} /> WiFi</span>} value={p[P.ssid]} />
                  <Field label="Firmware" value={p[P.fw]} />
                  <button onClick={reboot} data-testid="c360-reboot" className="mt-2 w-full bg-slate-700 hover:bg-slate-600 rounded py-1.5 text-[11px] flex items-center justify-center gap-1"><Power size={12} /> {t("Restart routera")}</button>
                </>
              ) : <div className="text-slate-500">{t("Brak przypisanego routera")}</div>}
              <div className="flex items-center justify-between gap-3 pt-2 mt-1 border-t border-slate-700/40" data-testid="c360-rx">
                <span className="text-slate-500 shrink-0 flex items-center gap-1"><Gauge size={11} /> {t("Sygnał RX (ONU)")}</span>
                <span className="flex items-center gap-1.5" title={rxTitle}>
                  <span className={`w-2.5 h-2.5 rounded-full ${rxDot}`} data-testid="c360-rx-dot" />
                  <span className="text-slate-200">{topo?.measured_rx_dbm != null ? `${topo.measured_rx_dbm} dBm` : "—"}</span>
                  <span className="text-[10px] text-slate-400">· {rxLabel}</span>
                </span>
              </div>
              {c.id && (
                <div className="pt-1" data-testid="c360-rx-history">
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-[10px] uppercase text-slate-500">{t("Historia RX")} ({rxHist.length})</span>
                    <div className="flex gap-1">
                      {["24h", "7d", "30d"].map((r) => (
                        <button key={r} onClick={() => setRxRange(r)} data-testid={`rx-range-${r}`} className={`text-[10px] px-1.5 py-0.5 rounded ${rxRange === r ? "bg-cyan-600 text-white" : "bg-slate-800 text-slate-400 hover:text-cyan-300"}`}>{r}</button>
                      ))}
                    </div>
                  </div>
                  {rxHist.length >= 2 ? (
                    <div style={{ width: "100%", height: 56 }}>
                      <ResponsiveContainer>
                        <LineChart data={rxHist} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                          <YAxis hide domain={["dataMin - 1", "dataMax + 1"]} />
                          <RTooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", fontSize: 11, borderRadius: 6 }} labelFormatter={() => ""} formatter={(v) => [`${v} dBm`, "RX"]} />
                          <Line type="monotone" dataKey="rx_dbm" stroke="#22d3ee" strokeWidth={2} dot={{ r: 2 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="text-[11px] text-slate-500 py-2">{t("Za mało danych w tym zakresie")}</div>
                  )}
                </div>
              )}
            </Card>

            <Card icon={Receipt} title={t("Faktury")} tint="text-amber-400" testid="card-invoices">
              {invoices.length ? invoices.slice(0, 4).map((inv) => (
                <div key={inv.id || inv.number} className="flex justify-between gap-2">
                  <span className="text-slate-400 truncate">{inv.number || (inv.created_at ? new Date(inv.created_at).toLocaleDateString("pl-PL") : "—")}</span>
                  <span className="text-slate-200">{inv.gross ?? inv.amount ?? inv.total ?? "—"} zł</span>
                  <span className={`text-[10px] px-1 rounded ${inv.status === "opłacona" ? "text-green-300" : "text-amber-300"}`}>{inv.status || ""}</span>
                </div>
              )) : <div className="text-slate-500">{t("Brak faktur")}</div>}
            </Card>

            <Card icon={TriangleAlert} title={t("Aktywne awarie")} tint={faults.length ? "text-rose-400" : "text-emerald-400"} testid="card-faults">
              {faults.length ? faults.slice(0, 5).map((f) => (
                <div key={f.id || f.ticket_no} className="flex justify-between gap-2">
                  <span className="text-slate-300 truncate">{f.ticket_no ? `${f.ticket_no} · ` : ""}{f.title || "—"}</span>
                  <span className="text-[10px] px-1 rounded text-rose-300 shrink-0">{f.status || ""}</span>
                </div>
              )) : <div className="text-emerald-400/80">{t("Brak aktywnych awarii")}</div>}
            </Card>

            <Card icon={MapPin} title={t("Sieć i lokalizacja")} tint="text-cyan-400" testid="card-network">
              <Field label={t("Współrzędne")} value={c.lat && c.lng ? `${Number(c.lat).toFixed(5)}, ${Number(c.lng).toFixed(5)}` : (c.coordinates || null)} />
              <Field label="OLT / Switch" value={topo?.olt || c.olt || c.switch} />
              <Field label={t("Port PON")} value={topo?.pon_port} />
              <Field label={t("Splitter")} value={topo?.splitter} />
              <Field label={t("Port splittera")} value={topo?.splitter_port} />
              <Field label={t("Kabel")} value={topo?.cable} />
              <Field label={t("Włókno")} value={topo?.fiber ?? c.fiber} />
              <Field label={t("Mufy")} value={topo?.closures?.length ? topo.closures.join(", ") : null} />
              <button onClick={showOnMap} data-testid="c360-map" className="mt-2 w-full bg-cyan-700 hover:bg-cyan-600 rounded py-1.5 text-[11px] flex items-center justify-center gap-1"><ExternalLink size={12} /> {t("Pokaż na mapie")}</button>
              {topo?.olt && (
                <button onClick={() => setProv({ olt_name: topo.olt, port: topo.pon_port, onu: c.onu_index ?? c.onu ?? "", vlan: c.vlan ?? c.vlan_id ?? "", client_id: c.id })}
                  data-testid="c360-provision" className="mt-1.5 w-full bg-cyan-600/80 hover:bg-cyan-500 rounded py-1.5 text-[11px] flex items-center justify-center gap-1"><TerminalSquare size={12} /> {t("Provisioning ONU")}</button>
              )}
              <div className="pt-1 text-[11px] text-slate-500">{t("Podłączenia sieciowe pobierane z topologii FiberPass.")}</div>
            </Card>
          </div>
        )}
      </div>
      {prov && <OltProvision initial={prov} defaultPort={prov.port ? String(prov.port) : ""} onClose={() => setProv(null)} />}
    </div>
  );
}
